import React, { useMemo } from "react";
import { useChallenges } from "../context/ChallengesContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Swords, Check, X, Crown } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * Active Challenges banner (pinned on Dashboard, also reusable inside the
 * Squad → Challenges tab if we want).
 *
 * Two card states:
 *   - Pending FOR ME → red Accept / X decline buttons
 *   - Active → live mini-scoreboard "Me 1250 kg · Buddy 980 kg"; updates in
 *     real time via the WebSocket fan-out from sessions.complete.
 *
 * Hidden when there's nothing to show — no empty state clutter.
 */
export default function ActiveChallengesBanner() {
  const { active, pendingForMe, refresh } = useChallenges();
  const { user } = useAuth();

  // Active sub-list = anything currently running (status active) involving me
  const live = useMemo(
    () => active.filter((c) => c.status === "active"),
    [active]
  );

  if (!user) return null;
  if (pendingForMe.length === 0 && live.length === 0) return null;

  const accept = async (c) => {
    try {
      await api.post(`/challenges/${c.challenge_id}/accept`);
      toast.success(`Challenge on! 🔥`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't accept"); }
  };
  const decline = async (c) => {
    try {
      await api.post(`/challenges/${c.challenge_id}/decline`);
      toast(`Declined ${c.challenger_name}`);
      refresh();
    } catch { toast.error("Couldn't decline"); }
  };

  return (
    <div data-testid="active-challenges-banner" className="mb-5 space-y-3">
      {pendingForMe.map((c) => (
        <div key={c.challenge_id} data-testid={`challenge-pending-${c.challenge_id}`} className="border border-blue-500/40 bg-blue-500/5">
          <div className="flex items-center gap-2 px-4 py-2 border-b border-blue-500/20 text-blue-500 text-xs tracking-[0.3em] uppercase font-bold">
            <Swords className="w-3 h-3" /> Challenge from {c.challenger_name}
          </div>
          <div className="p-4 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="font-display text-base uppercase truncate">{c.title}</div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-0.5">{c.duration_days} days</div>
            </div>
            <div className="flex gap-1.5 shrink-0">
              <Button size="sm" data-testid={`challenge-accept-${c.challenge_id}`} onClick={() => accept(c)} className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-8">
                <Check className="w-3 h-3 mr-1" /> Accept
              </Button>
              <Button size="icon" variant="ghost" data-testid={`challenge-decline-${c.challenge_id}`} onClick={() => decline(c)} title="Decline" className="text-neutral-400 hover:text-blue-500 h-8 w-8">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      ))}

      {live.map((c) => {
        const iAmChallenger = c.challenger_id === user.user_id;
        const myScore = iAmChallenger ? c.challenger_score : c.opponent_score;
        const theirScore = iAmChallenger ? c.opponent_score : c.challenger_score;
        const theirName = iAmChallenger ? c.opponent_name : c.challenger_name;
        const ahead = myScore > theirScore;
        const tied = myScore === theirScore;
        const unit = c.challenge_type === "volume" ? "kg" : c.challenge_type === "bodyweight_loss" ? "kg lost" : c.challenge_type === "reps" ? "reps" : "wkts";
        return (
          <div key={c.challenge_id} data-testid={`challenge-active-${c.challenge_id}`} className="border border-neutral-800 bg-neutral-900/40">
            <div className="flex items-center gap-2 px-4 py-2 border-b border-neutral-800 text-neutral-400 text-xs tracking-[0.3em] uppercase font-bold">
              <Swords className="w-3 h-3" /> {c.title}
            </div>
            <div className="grid grid-cols-2 divide-x divide-neutral-800">
              <div className={`p-3 text-center ${ahead && !tied ? "bg-blue-500/10" : ""}`}>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">You</div>
                <div className="font-display text-2xl text-white">{Math.round(myScore || 0)}</div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">{unit}</div>
                {ahead && !tied && <Crown className="w-3 h-3 text-blue-500 mx-auto mt-1" />}
              </div>
              <div className={`p-3 text-center ${!ahead && !tied ? "bg-blue-500/10" : ""}`}>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 truncate">{theirName}</div>
                <div className="font-display text-2xl text-white">{Math.round(theirScore || 0)}</div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">{unit}</div>
                {!ahead && !tied && <Crown className="w-3 h-3 text-blue-500 mx-auto mt-1" />}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
